import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Pill } from '@ui'
import { getOrg, listAuditEvents, resendOrgInvite } from '../api.js'
import { Avatar } from '../components/Avatar.jsx'
import { ActionBadge } from '../components/ActionBadge.jsx'
import { InviteAdminModal } from './InviteAdminModal.jsx'
import { RevokeOrgModal } from './RevokeOrgModal.jsx'

export function OrgDetail({ showInvite = false, showRevoke = false }) {
  const { orgId } = useParams()
  const nav = useNavigate()
  const [org, setOrg] = useState(null)
  const [audit, setAudit] = useState([])
  const [err, setErr] = useState(null)
  const [resendingEmail, setResendingEmail] = useState(null)
  const [toast, setToast] = useState(null)

  const refresh = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([getOrg(orgId), listAuditEvents(orgId)])
      setOrg(t)
      setAudit(a.slice(0, 20))
    } catch (e) { setErr(e.message || 'Failed to load org.') }
  }, [orgId])

  useEffect(() => { refresh() }, [refresh])

  useEffect(() => {
    if (!toast) return
    const id = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(id)
  }, [toast])

  async function resend(email) {
    setResendingEmail(email)
    setToast(null)
    try {
      await resendOrgInvite(orgId, { email })
      setToast({ tone: 'success', message: `Invite re-sent to ${email}.` })
      refresh()
    } catch (e) {
      setToast({ tone: 'danger', message: e.message || 'Resend failed.' })
    } finally {
      setResendingEmail(null)
    }
  }

  if (err) return <p style={{ color: 'var(--danger-text)' }}>{err}</p>
  if (!org) return <p>Loading…</p>

  return (
    <>
      <header style={styles.header}>
        <Avatar name={org.name} size={48} />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', fontSize: 20, fontWeight: 600 }}>
            <span>{org.name}</span>
            <span style={styles.slug}>{org.slug}</span>
          </h2>
          <p style={styles.sub}>
            {org.userCount} users · created {formatDate(org.createdAt)}
          </p>
        </div>
        <Button variant="danger" onClick={() => nav(`/orgs/${orgId}/revoke`)}>Revoke org</Button>
      </header>

      {toast && (
        <div
          role="status"
          style={{
            ...styles.toast,
            background: toast.tone === 'success' ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: toast.tone === 'success' ? 'var(--success-text)' : 'var(--danger-text)',
            borderColor: toast.tone === 'success' ? 'var(--success-border)' : 'var(--danger-border)',
          }}
        >
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card style={{ padding: 16 }}>
          <div style={styles.cardHeader}>
            <h3 style={{ margin: 0 }}>Admins</h3>
            <Button size="sm" onClick={() => nav(`/orgs/${orgId}/invite`)}>Invite admin</Button>
          </div>
          {org.admins.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No admins yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 48 }} />
                <col />
                <col style={{ width: '28%' }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 110 }} />
              </colgroup>
              <tbody>
                {org.admins.map((a) => (
                  <tr key={a.userId} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 8px' }}>
                      <Avatar name={a.name || a.email} size={28} />
                    </td>
                    <td style={{ ...adminTd, fontWeight: 500 }}>
                      {a.name || <span style={{ opacity: 0.5 }}>—</span>}
                    </td>
                    <td style={{ ...adminTd, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.email}>
                      {a.email}
                    </td>
                    <td style={adminTd}><RolePill role={a.role} /></td>
                    <td style={adminTd}><AdminStatusPill status={a.status} /></td>
                    <td style={{ ...adminTd, textAlign: 'right' }}>
                      {a.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => resend(a.email)}
                          disabled={resendingEmail === a.email}
                        >
                          {resendingEmail === a.email ? 'Resending…' : 'Resend'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Recent activity</h3>
          {audit.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No audit events yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th style={auditTh}>Time</th>
                  <th style={auditTh}>Action</th>
                  <th style={auditTh}>Detail</th>
                  <th style={auditTh}>Actor</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={`${e.ts}-${e.action}`} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...auditTd, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {new Date(e.ts).toLocaleString()}
                    </td>
                    <td style={auditTd}><ActionBadge action={e.action} /></td>
                    <td style={auditTd}>{e.detail}</td>
                    <td style={{ ...auditTd, opacity: 0.7 }}>{e.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {showInvite && (
        <InviteAdminModal
          orgId={orgId}
          onClose={() => nav(`/orgs/${orgId}`)}
          onInvited={refresh}
        />
      )}
      {showRevoke && (
        <RevokeOrgModal
          org={org}
          onClose={() => nav(`/orgs/${orgId}`)}
          onRevoked={() => nav('/orgs')}
        />
      )}
    </>
  )
}

function RolePill({ role }) {
  return <Pill tone="info">{role}</Pill>
}

function AdminStatusPill({ status }) {
  if (status === 'pending') return <Pill tone="info">Invite pending</Pill>
  return <Pill tone="success">Active</Pill>
}

const adminTd = { padding: '10px 8px', verticalAlign: 'middle' }
const auditTh = { padding: '8px 12px', fontWeight: 600, fontSize: 12, opacity: 0.7 }
const auditTd = { padding: '10px 12px' }

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  slug: {
    fontStyle: 'italic',
    fontSize: 14,
    fontWeight: 400,
    color: 'var(--text-secondary)',
  },
  sub: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    gap: 12,
  },
  toast: {
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid',
    marginBottom: 16,
    fontSize: 13,
  },
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
